import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Clock, Download, EyeOff, Lock, MonitorPlay, Plus, Shuffle, Users, Zap,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button, Badge } from '../../components/ui';
import { STAT_CARD_BASE, STAT_CHIP } from '../../components/ui/StatCard';
import KpiStrip from '../../components/simulation/KpiStrip';
import ScenarioBrief from '../../components/simulation/ScenarioBrief';
import { FINAL_VIEWS } from '../../components/simulation/FinalDebrief';
import useWebSocket from '../../hooks/useWebSocket';
import useCountdown, { formatClock } from '../../hooks/useCountdown';
import simulationRunService from '../../services/simulationRunService';
import { showError, showSuccess } from '../../utils/toast';
import { cardById, movementSense, formatDelta } from '../../utils/simulationEngine';

/**
 * Trainer live console — the facilitator's private screen.
 *
 * This is NOT the projector. It carries the roster, per-team lock status and the actual
 * cards teams have chosen before the reveal, none of which may ever be shown to the
 * room. A separate read-only projector surface is the right next thing to build.
 *
 * Live commits arrive on `sim:facilitator-commit`, which the server sends only to the
 * facilitator room.
 */

const PHASES = [
  { key: 'assign', label: 'Assign' },
  { key: 'brief', label: 'Brief' },
  { key: 'negotiate', label: 'Negotiate' },
  { key: 'decide', label: 'Decide' },
  { key: 'reveal', label: 'Reveal' },
  { key: 'debrief', label: 'Debrief' },
];

const SimulationConsolePage = () => {
  const { runId } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [eventCardId, setEventCardId] = useState(null);
  const [teams, setTeams] = useState([]);
  /** Cards seen live over the facilitator channel, before the round resolves. */
  const [liveCommits, setLiveCommits] = useState({});

  const socket = useWebSocket(run?.sessionId || null);
  const { on, off, emit, isConnected } = socket;

  const load = useCallback(async () => {
    try {
      const res = await simulationRunService.get(runId);
      setRun(res.run);
      setSimulation(res.simulation);
    } catch (err) {
      showError(err.userMessage || 'Could not load this run');
      navigate('/trainer/simulations/team');
    } finally {
      setLoading(false);
    }
  }, [runId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isConnected && runId) emit('sim:watch', { runId });
  }, [isConnected, runId, emit]);

  useEffect(() => {
    const onCommit = ({ roundIndex, functionKey, cardId }) =>
      setLiveCommits((c) => ({ ...c, [`${roundIndex}:${functionKey}`]: cardId }));
    const onStatus = (status) =>
      setRun((r) => (r && String(status.runId) === String(r.id)
        ? { ...r, lockedCount: status.lockedCount, lockedFunctionKeys: status.lockedFunctionKeys }
        : r));
    on('sim:facilitator-commit', onCommit);
    on('sim:submission-status', onStatus);
    return () => { off('sim:facilitator-commit', onCommit); off('sim:submission-status', onStatus); };
  }, [on, off]);

  const call = async (fn, failure) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.run) setRun(res.run);
      return res;
    } catch (err) {
      if (err.status === 409 && err.data?.run) setRun(err.data.run);
      else showError(err.userMessage || err.message || failure);
      return null;
    } finally { setBusy(false); }
  };

  const assign = async () => {
    const res = await call(() => simulationRunService.assign(runId), 'Could not assign teams');
    if (res) { setTeams(res.teams || []); showSuccess(`${res.assigned} participants assigned`); }
  };

  const round = useMemo(
    () => simulation?.rounds.find((r) => r.index === run?.roundIndex) || null,
    [simulation, run]
  );

  const resolved = useMemo(() => {
    if (!simulation || !run) return null;
    const result = run.roundResults?.find((r) => r.roundIndex === run.roundIndex);
    if (!result) return null;
    return {
      ...result,
      attribution: result.perFunction.map((p) => {
        const fn = simulation.functions.find((f) => f.key === p.functionKey);
        return { ...p, functionName: fn?.name, variant: fn?.variant, card: cardById(simulation, p.cardId) };
      }),
    };
  }, [simulation, run]);

  const timed = ['negotiate', 'decide'].includes(run?.phase);
  const secondsLeft = useCountdown(timed ? run?.phaseEndsAt : null, run?.serverNow);

  if (loading || !run || !simulation) {
    return (
      <DashboardLayout title="Live console">
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto">
          <div className={`${STAT_CARD_BASE} animate-pulse p-8`}>
            <div className="h-5 w-1/3 rounded bg-neutral-200 dark:bg-neutral-800" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const lockedKeys = run.lockedFunctionKeys || run.commits?.map((c) => c.functionKey) || [];
  const allIn = lockedKeys.length >= run.teamCount;
  const isLastRound = run.roundIndex >= simulation.rounds.length;

  const advance = () => {
    const p = run.phase;
    if (p === 'assign') return call(() => simulationRunService.advancePhase(runId, { from: 'assign', to: 'brief' }), 'Could not advance');
    if (p === 'brief') return call(() => simulationRunService.advancePhase(runId, { from: 'brief', to: simulation.play?.negotiateEnabled ? 'negotiate' : 'decide' }), 'Could not advance');
    if (p === 'negotiate') return call(() => simulationRunService.advancePhase(runId, { from: 'negotiate', to: 'decide' }), 'Could not advance');
    if (p === 'decide') {
      return call(async () => {
        await simulationRunService.resolve(runId, eventCardId);
        return simulationRunService.advancePhase(runId, { from: 'decide', to: 'reveal' });
      }, 'Could not reveal');
    }
    if (p === 'reveal') return call(() => simulationRunService.advancePhase(runId, { from: 'reveal', to: 'debrief' }), 'Could not advance');
    if (isLastRound) return call(() => simulationRunService.end(runId), 'Could not close the run');
    setEventCardId(null);
    setLiveCommits({});
    return call(() => simulationRunService.advancePhase(runId, { from: 'debrief', to: 'brief', roundIndex: run.roundIndex + 1 }), 'Could not start the next round');
  };

  return (
    <DashboardLayout title="Live console">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">

        <div className="flex items-start gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2.5 dark:border-neutral-700/60 dark:bg-neutral-800/50">
          <EyeOff size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-neutral-400" />
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            <span className="font-semibold">Your screen only.</span> This shows what each team
            picked before the reveal — don&apos;t project it.
          </p>
        </div>

        <div className={`${STAT_CARD_BASE} p-4 sm:p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="primary" size="sm">Round {run.roundIndex} of {simulation.rounds.length}</Badge>
                <Badge variant={run.mode === 'live' ? 'live' : 'default'} size="sm" dot>
                  {run.mode === 'live' ? 'Live' : 'Rehearsal'}
                </Badge>
                {!isConnected && <Badge variant="warning" size="sm">Offline</Badge>}
              </div>
              <h2 className="mt-2 text-base font-semibold text-neutral-900 dark:text-neutral-100">{round?.title}</h2>
            </div>
            {/* Opens in its own window so it can be dragged to the second display —
                the room sees the projector, never this console. */}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!run.roundResults?.length}
                title={!run.roundResults?.length ? 'Play a round first' : undefined}
                onClick={async () => {
                  try { await simulationRunService.downloadReport(runId); }
                  catch (e) { showError(e.userMessage || 'Could not build the report'); }
                }}
              >
                <Download size={14} strokeWidth={2} className="mr-1.5" />
                Debrief PDF
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(`/trainer/simulation-runs/${runId}/projector`, 'aktivity-projector', 'noopener')}
              >
                <MonitorPlay size={14} strokeWidth={2} className="mr-1.5" />
                Open room screen
              </Button>
              {/* The run knows its session, but nothing linked back to it — a trainer who
                  came here mid-session had no way to return to the live activity controls
                  short of editing the URL. Only rendered for live runs; a rehearsal has
                  no session attached. */}
              {run.sessionId && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/trainer/session/${run.sessionId}/control`)}
                >
                  <ArrowLeft size={14} strokeWidth={2} className="mr-1.5" />
                  Back to session
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-1.5 overflow-x-auto pb-1">
            {PHASES.map((p) => (
              <span
                key={p.key}
                className={`shrink-0 rounded-lg px-2.5 py-1 text-[11px] font-semibold ${
                  run.phase === p.key
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-400'
                    : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500'
                }`}
              >
                {p.label}
              </span>
            ))}
          </div>

          {/* The negotiate/decide clock, as the room and every phone see it. Running out
              never advances anything — it tells the trainer to wrap the room up. */}
          {typeof secondsLeft === 'number' && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-neutral-50 px-4 py-3 dark:bg-neutral-800/60">
              <span className="flex items-center gap-2">
                <Clock size={15} strokeWidth={1.75} className="text-neutral-400" />
                <span className={`text-lg font-bold tabular-nums ${
                  secondsLeft === 0 ? 'text-red-600 dark:text-red-400'
                    : secondsLeft <= 30 ? 'text-amber-600 dark:text-amber-400'
                    : 'text-neutral-900 dark:text-neutral-100'
                }`}
                >
                  {secondsLeft === 0 ? "Time's up" : formatClock(secondsLeft)}
                </span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {run.phase === 'negotiate' ? 'to negotiate' : 'to decide'}
                </span>
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => call(() => simulationRunService.extend(runId, 60), 'Could not add time')}
              >
                <Plus size={14} strokeWidth={2} className="mr-1.5" />
                1 minute
              </Button>
            </div>
          )}
        </div>

        <KpiStrip
          kpis={simulation.kpis}
          values={run.cumulative}
          deltas={['reveal', 'debrief'].includes(run.phase) ? resolved?.total : null}
          compact
        />

        {/* ---------- teams ---------- */}
        <div className={`${STAT_CARD_BASE} p-5`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <Users size={15} strokeWidth={1.75} className="text-primary-500" />
              Teams
            </h3>
            <Button variant="secondary" size="sm" onClick={assign} disabled={busy}>
              <Shuffle size={14} strokeWidth={2} className="mr-1.5" />
              Auto-assign
            </Button>
          </div>

          <div className="grid gap-2.5 sm:grid-cols-2">
            {simulation.functions.map((fn) => {
              const locked = lockedKeys.includes(fn.key);
              const liveCard = liveCommits[`${run.roundIndex}:${fn.key}`];
              const committed = run.commits?.find((c) => c.functionKey === fn.key);
              const card = cardById(simulation, liveCard || committed?.cardId);
              const size = teams.find((t) => t.functionKey === fn.key)?.count;

              return (
                <div
                  key={fn.key}
                  className={`rounded-xl border p-3.5 transition-colors ${
                    locked ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/5'
                    : 'border-neutral-200 dark:border-neutral-700/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[fn.variant]}`}>
                      {fn.name}
                    </span>
                    {locked
                      ? <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400"><Check size={12} strokeWidth={3} />Locked</span>
                      : <span className="text-[11px] text-neutral-400">Deciding…</span>}
                  </div>
                  {typeof size === 'number' && (
                    <p className="mt-1.5 text-[11px] text-neutral-400">{size} {size === 1 ? 'person' : 'people'}</p>
                  )}
                  {card && (
                    <p className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">{card.title}</p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
            {lockedKeys.length} of {run.teamCount} teams have locked in
          </p>
        </div>

        {/* ---------- phase body ---------- */}
        {run.phase === 'brief' && round && (
          <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
            <ScenarioBrief round={round} functions={simulation.functions} />
          </div>
        )}

        {run.phase === 'decide' && simulation.eventCards?.length > 0 && (
          <div className={`${STAT_CARD_BASE} p-5`}>
            <div className="flex items-start gap-2.5">
              <Zap size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-500" />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  Play a {simulation.meta.eventDeckLabel}?
                </h3>
                <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                  Fires when you reveal, and hits every team at once.
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setEventCardId(null)}
                    className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                      eventCardId === null
                        ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                        : 'bg-white text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                    }`}
                  >
                    Nothing this round
                  </button>
                  {simulation.eventCards.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      title={e.effectOnTeams}
                      onClick={() => setEventCardId(e.id)}
                      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                        eventCardId === e.id
                          ? 'bg-amber-500 text-white'
                          : 'bg-white text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                      }`}
                    >
                      {e.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {['reveal', 'debrief'].includes(run.phase) && resolved && (
          <div className={`${STAT_CARD_BASE} p-5`}>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">What each team chose</h3>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                The room sees the choices and the numbers now. The insights stay on your screen
                until the final debrief, after the last round.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {resolved.attribution.map((a) => (
                <div key={a.functionKey} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
                  <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[a.variant] || STAT_CHIP.slate}`}>
                    {a.functionName}
                  </span>
                  <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{a.card?.title}</h4>
                  {a.card?.leadershipAngle && (
                    <p className="mt-1.5 text-xs font-medium text-primary-700 dark:text-primary-400">{a.card.leadershipAngle}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1">
                    {simulation.kpis.map((kpi) => {
                      const d = a.kpi?.[kpi.key];
                      if (!d) return null;
                      const sense = movementSense(kpi, d);
                      return (
                        <span key={kpi.key} className="text-[11px]">
                          <span className="text-neutral-400">{kpi.label} </span>
                          <span className={`font-semibold tabular-nums ${
                            sense === 'good' ? 'text-emerald-600 dark:text-emerald-400'
                            : sense === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-neutral-400'}`}>
                            {formatDelta(d)}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The trainer's own debrief script for this round. Never on the room screen:
            the room gets every teaching point together, in the final debrief. */}
        {['reveal', 'debrief'].includes(run.phase) && round?.debrief
          && (round.debrief.teachingPoint || round.debrief.facilitatorQuestions?.length > 0) && (
          <div className={`${STAT_CARD_BASE} p-5`}>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <Lock size={14} strokeWidth={1.75} className="text-neutral-400" />
              Your debrief notes
            </h3>
            {round.debrief.teachingPoint && (
              <div className="mt-3 rounded-xl border-l-4 border-primary-500 bg-primary-50/60 px-4 py-3 dark:bg-primary-500/10">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-400">The point</p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-800 dark:text-neutral-100">{round.debrief.teachingPoint}</p>
              </div>
            )}
            {round.debrief.facilitatorQuestions?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {round.debrief.facilitatorQuestions.map((q) => (
                  <li key={q.text} className="flex gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                    {q.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* After the last round: step the room screen through the final debrief from
            here, so nobody has to reach for the projected window. */}
        {run.phase === 'closed' && (
          <div className={`${STAT_CARD_BASE} p-5`}>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <MonitorPlay size={15} strokeWidth={1.75} className="text-primary-500" />
              Final debrief on the room screen
            </h3>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Round by round first, then where the numbers ended up, then the learnings — every
              teaching point and the insight on each team&apos;s choices.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {FINAL_VIEWS.map((v, i) => (
                <Button
                  key={v.key}
                  variant={run.finalView === v.key ? 'primary-filled' : 'secondary'}
                  size="sm"
                  disabled={busy}
                  onClick={() => call(() => simulationRunService.setFinalView(runId, v.key), 'Could not change the room screen')}
                >
                  {i + 1}. {v.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* A closed run has nowhere left to advance to. The label below is a chain of
            per-phase cases with no 'closed' branch, so it used to render an unlabelled
            arrow that fired advance() anyway — the server rejected it with a 409 and the
            trainer got a dead control. Offer the way out instead. */}
        {run.phase === 'closed' ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-neutral-400 dark:text-neutral-500">
              This run has finished. The debrief PDF stays available.
            </p>
            {run.sessionId && (
              <Button variant="primary-filled" onClick={() => navigate(`/trainer/session/${run.sessionId}/control`)}>
                <ArrowLeft size={15} strokeWidth={2} className="mr-1.5" />
                Back to session
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-neutral-400 dark:text-neutral-500">
              {run.phase === 'decide' && !allIn
                ? `Waiting on ${run.teamCount - lockedKeys.length} team${run.teamCount - lockedKeys.length === 1 ? '' : 's'}`
                : 'You control the pace.'}
            </p>
            <Button variant="primary-filled" onClick={advance} loading={busy} disabled={busy || (run.phase === 'decide' && !allIn)}>
              {run.phase === 'assign' && 'Start round 1'}
              {run.phase === 'brief' && (simulation.play?.negotiateEnabled ? 'Open negotiation' : 'Open decisions')}
              {run.phase === 'negotiate' && 'Open decisions'}
              {run.phase === 'decide' && 'Reveal all teams'}
              {run.phase === 'reveal' && 'Go to debrief'}
              {run.phase === 'debrief' && (isLastRound ? 'Finish and show the final debrief' : `Start round ${run.roundIndex + 1}`)}
              <ArrowRight size={15} strokeWidth={2} className="ml-1.5" />
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SimulationConsolePage;
